import React, { useState, useEffect } from 'react';
import { PrismButton, PrismBanner } from '@prism-ui/react';
import './RelationshipStep.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronRight,
  faChevronLeft,
} from '@fortawesome/free-solid-svg-icons';
import ApplicationHeader from '../ApplicationHeader.jsx';

const RelationshipStep = ({
  application,
  saveApplication,
  jumpToStep,
  hideNavigation = false,
}) => {
  const thisPageStep = 4;

  // State
  const [relationshipCount, setRelationshipCount] = useState(
    parseInt(application?.relationship_count, 10) || 1
  );
  const [showConfirmation, setShowConfirmation] = useState(false);

  if(application?.applicant_type_id === 2){  // BUG FIX: Use strict equality
    var role = "mentor";
  }
  else if(application?.applicant_type_id === 3){  // BUG FIX: Use strict equality
    role = "committee member";
  }

  // Load existing relationship count on mount
  useEffect(() => {
    if (application?.applicant_type_id === 1) {
      console.log('Skipping RelationshipStep for applicant_type_id 1');
      jumpToStep(thisPageStep + 1);
    }
    if (application?.relationship_count) {
      const count = parseInt(application.relationship_count, 10) || 1;
      setRelationshipCount(count);
    }
  }, [application?.relationship_count, application?.applicant_type_id, jumpToStep]);

  const onContinue = async () => {
    try {
      jumpToStep(thisPageStep + 1);
    } catch (error) {
      console.error('onContinue - failed:', error);
    }
  };

  const onBack = async () => {
    try {
      jumpToStep(thisPageStep - 1);
    } catch (error) {
      console.error('onBack - failed:', error);
    }
  };

  const handleRelationshipCountChange = async (newValue) => {
    const newCount = parseInt(newValue, 10);
    setRelationshipCount(newCount);

    try {
      await saveApplication({
        ...application,
        relationship_count: newCount,
      });
      console.log('Relationship count saved:', newCount);
      
      // Show confirmation pop-up only when navigation is hidden
      if (hideNavigation) {
        setShowConfirmation(true);
        // Auto-hide after 2 seconds
        setTimeout(() => {
          setShowConfirmation(false);
        }, 2000);
      }
    } catch (error) {
      console.error('Error saving relationship count:', error);
    }
  };

  const CustomSegmentedSlider = () => {
    const getBackgroundColor = (value) => {
      switch (value) {
        case 1:
          return 'var(--color-success)';
        case 2:
          return 'var(--color-warning)';
        case 3:
          return 'var(--color-danger)';
        default:
          return 'var(--color-disabled)';
      }
    };

    // All three segment backgrounds are fixed, saturated, light-ish accents that do not flip
    // between themes, so their label must not flip either. Theme tokens fail at both ends:
    // --color-surface is white in light mode (2.15:1 on green) and --color-text is near-white
    // in dark mode (1.45:1 on amber). A fixed near-black clears AA on all three
    // (success 7.19, warning 8.36, danger 4.81).
    const ACTIVE_SEGMENT_TEXT = '#212529';

    return (
      <div className="custom-segmented-slider">
        <div className="slider-track">
          {[1, 2, 3].map((value) => (
            <div
              key={value}
              className={`slider-segment ${relationshipCount === value ? 'active' : ''}`}
              style={{
                backgroundColor:
                  relationshipCount === value
                    ? getBackgroundColor(value)
                    : 'var(--color-surface-alt)',
                color:
                  relationshipCount === value ? ACTIVE_SEGMENT_TEXT : 'var(--color-text-faded)',
                border:
                  relationshipCount === value
                    ? `2px solid ${getBackgroundColor(value)}`
                    : '2px solid var(--color-border)',
              }}
              onClick={() => handleRelationshipCountChange(value)}
            >
              <div className="segment-content">
                <div className="segment-number">{value}</div>
                <div className="segment-label">
                  {value === 1 ? 'Relationship' : 'Relationships'}
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className="slider-description">
          <small style={{ color: 'var(--color-text-faded)' }}>
            Click on a segment to select the number of relationships
          </small>
        </div>
      </div>
    );
  };

  const createRelationshipSelector = () => {

    return (
      <section className="relationship-panel">
        <h2 className="relationship-panel__heading">
          Please select the number of relationships you would like to
          participate in:
        </h2>
        <div className="relationship-panel__body">
          <div className="relationship-selector">
            <h5 style={{ marginBottom: '1rem' }}>Number of Relationships</h5>

            <CustomSegmentedSlider />

            {relationshipCount === 1 && (
              <h6 style={{ textAlign: 'center' }}>
                As a {role}, I want a relationship with {relationshipCount} mentee this year.
              </h6>
            )}
            {relationshipCount > 1 && (
              <h6 style={{ textAlign: 'center' }}>
                As a {role}, I want relationships
                with {relationshipCount} mentees this year.
              </h6>
            )}
          </div>
        </div>
      </section>
    );
  };

  return (
    <div>
      <ApplicationHeader message={'Relationships'} />

      <div>{createRelationshipSelector()}</div>
      
      {/* Confirmation Alert */}
      {showConfirmation && (
        <div 
          style={{ 
            position: 'fixed', 
            top: '20px', 
            left: '50%', 
            transform: 'translateX(-50%)', 
            zIndex: 1050, 
            minWidth: '300px', 
            maxWidth: '400px' 
          }}
        >
          <PrismBanner 
            tone="success" 
            layout="inline"
            dismissible
          >
            Selection saved successfully!
          </PrismBanner>
        </div>
      )}
      
      {!hideNavigation && (
        <div className="nav-btns">
          <PrismButton tone="primary-brand" size="lg" onPrismClick={onBack}>
            <FontAwesomeIcon icon={faChevronLeft} /> Back
          </PrismButton>
          <PrismButton tone="primary-brand" size="lg" onPrismClick={onContinue}>
            Continue <FontAwesomeIcon icon={faChevronRight} />
          </PrismButton>
        </div>
      )}
    </div>
  );
};

export default RelationshipStep;